import * as React from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { Menu01Icon, SidebarLeftIcon } from '@hugeicons/core-free-icons';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  getNavigationCommands,
  getPrimarySidebarTree,
  getSettingsCommands,
} from '@/constants/navigation/navConfig';
import { selectPlatform } from '@/redux/slices/platform/platformSlice';

const textOf = node => (typeof node === 'string' ? node : null);

/**
 * Breadcrumb trail for the current route, read from the sidebar's own nav tree
 * (display only). It starts at the section you are in, e.g. Collections / Knowledge Base,
 * then adds the page you opened inside it. Only the home page shows "Dashboard".
 */
function useCrumbs() {
  const { pathname, search } = useLocation();
  const platform = useSelector(selectPlatform);

  return React.useMemo(() => {
    if (pathname === '/') return [{ label: 'Dashboard', path: '/' }];

    const tree = getPrimarySidebarTree(platform);
    const firstSegment = pathname.split('/')[1] || '';
    const segment = path => (path || '').split('/')[1];
    const sameSection = path => path && path !== '/' && segment(path) === firstSegment;
    const lastSegment = pathname.split('/').filter(Boolean).pop() || '';
    let pageName = lastSegment
      .replace(/[-_]+/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^./, c => c.toUpperCase());
    // "Create" with a record id is really that record's edit page.
    if (pageName === 'Create' && new URLSearchParams(search).get('_id')) pageName = 'Edit';
    const subPage = landing =>
      landing !== pathname ? [{ label: pageName, path: `${pathname}${search}` }] : [];

    // 1. Exact match in the nav tree; 2. same section, named after the route.
    for (const exact of [true, false]) {
      for (const item of tree) {
        const child = item.children?.find(c => (exact ? c.path === pathname : sameSection(c.path)));
        if (child) {
          const crumbs = [];
          // A section's own "Dashboard" page is the section itself: Agents, not Agents / Dashboard.
          const isSectionHome = child.label === 'Dashboard';
          if (textOf(item.label)) {
            crumbs.push({ label: item.label, path: item.children[0]?.path || child.path });
          }
          if (!isSectionHome || !textOf(item.label)) {
            crumbs.push({ label: child.label, path: child.path });
          }
          return [...crumbs, ...subPage(child.path)];
        }
        if (
          item.path &&
          item.path !== '/' &&
          (exact ? item.path === pathname : sameSection(item.path))
        ) {
          return [{ label: item.label, path: item.path }, ...subPage(item.path)];
        }
      }
    }

    const command = [...getSettingsCommands(), ...getNavigationCommands(platform)].find(c =>
      sameSection(c.path)
    );
    if (command) {
      const crumbs = command.group === 'Settings' ? [{ label: 'Settings', path: '/profile' }] : [];
      return [...crumbs, { label: command.label, path: command.path }, ...subPage(command.path)];
    }
    return [{ label: pageName || 'Dashboard', path: pathname }];
  }, [pathname, search, platform]);
}

function AppBreadcrumbs() {
  const crumbs = useCrumbs();
  return (
    <Breadcrumb className="min-w-0">
      <BreadcrumbList className="min-w-0 flex-nowrap gap-1 text-[0.8rem] sm:gap-1.5 sm:text-sm">
        {crumbs.map((crumb, index) => {
          const last = index === crumbs.length - 1;
          // Phones keep just the page; the trail above it is in the sidebar.
          const phoneHidden = !last && 'max-sm:hidden';
          return (
            <React.Fragment key={`${crumb.label}-${index}`}>
              <BreadcrumbItem className={cn(last ? 'min-w-0' : 'shrink-0', phoneHidden)}>
                {last ? (
                  <BreadcrumbPage className="truncate font-medium">{crumb.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.path}>{crumb.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!last && <BreadcrumbSeparator className={cn('shrink-0', phoneHidden)} />}
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

/**
 * App header: sticky, translucent, 56px. Sidebar toggle (a menu button that opens the
 * sidebar sheet on phones), breadcrumbs, then the app's action buttons on the right.
 */
export default function AppHeader({
  collapsed,
  onToggleSidebar,
  showToggle = true,
  mobile = false,
  actions,
  className,
}) {
  const toggleLabel = mobile ? 'Open menu' : collapsed ? 'Expand sidebar' : 'Collapse sidebar';

  return (
    <TooltipProvider>
      <header
        data-slot="app-header"
        className={cn(
          'app-header sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b border-border/70 bg-background/85 px-4 backdrop-blur-xl backdrop-saturate-150 md:rounded-t-2xl md:px-5',
          className
        )}
      >
        {showToggle && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="-ms-1"
                  aria-label={toggleLabel}
                  onClick={onToggleSidebar}
                >
                  <HugeiconsIcon icon={mobile ? Menu01Icon : SidebarLeftIcon} strokeWidth={2} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">{toggleLabel}</TooltipContent>
            </Tooltip>
            <Separator orientation="vertical" className="me-1 h-4 self-center!" />
          </>
        )}
        <AppBreadcrumbs />
        <div className="ms-auto flex shrink-0 items-center gap-1">{actions}</div>
      </header>
    </TooltipProvider>
  );
}
